import { KeyValue } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ElementRef } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TokenStorage } from '@app/core/token.storage';
import { IButton, ITableButtons } from '@app/modules/table/table.interface';
import { SortOrder } from '@constants/mainConstants';
import { ISort } from '@interfaces/main/sort.interface';
import { uniqueFieldValidator } from '@modules/factor/children/factor-kanban/validators/validators';
import { EFactorDataStatuses } from '@modules/factor/enums/factor.enums';
import { NotifierService } from '@services/notifier.service';
import { throwError } from 'rxjs';
import { IAppServiceLanguages } from '../app.interface';
import { AppService } from '../app.service';
/**
 * Класс хелперов
 * @deprecated будет перегруппирован и заменен на src/shared/helpers/
 */
export class AppHelper {
    /** Дефолтный набор инвалидных селекторов */
    private static readonly invalidSelectors: string[] = [
        '.mat-input-element.ng-invalid',
        '.mat-select.ng-invalid',
        '.mat-checkbox-input.ng-invalid',
    ];

    /**
     * Проставление фокуса в поле
     * @param target элемент формы
     */
    static focusInput(target) {
        if (!target) {
            return;
        }
        const position = target.getBoundingClientRect();
        const height = position.top;
        if (target && target.querySelector && target.querySelector('input')) {
            target.querySelector('input').focus();
        }
        if (target && target.querySelector && target.querySelector('mat-select')) {
            target.querySelector('mat-select').click();
        }
        if (
            (target && target.localName === 'mat-select') ||
            (target && target.localName === 'input') ||
            (target && target.localName === 'mat-error')
        ) {
            if (height === 0) {
                const top = this.getParentElement(target);
                window.scrollTo(0, window.scrollY + top);
            } else if (height < 0.2 * window.innerHeight) {
                window.scrollTo(0, height);
                target.focus();
            } else {
                target.focus({ preventScroll: false });
                target.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }
            target.click();
        } else if (target) {
            if (target) {
                target.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }
            target.focus();
        }
    }

    /**
     * Создание и скачивание файла с сервера
     * @param body тело файла
     * @param options опции
     * @param filename имя файла
     * @deprecated
     */
    static createAndDownloadBlobFile(body, options, filename) {
        const blob = new Blob([body], options);

        const link = document.createElement('a');
        // Браузеры, которые поддерживают атрибут загрузки HTML5
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }

    /**
     * Получение имени файла из заголовков
     * @param headers заголовки ответа
     */
    static getHeaderFileName(headers) {
        let filename = 'temp';
        const disposition = headers.get('content-disposition');
        if (disposition && (disposition.indexOf('inline') !== -1 || disposition.indexOf('attachment') !== -1)) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, '');
            }
        }
        filename = decodeURI(filename);
        return filename;
    }

    /**
     * Проставление для всех полей формы признака изменения
     * @param formGroup объект формы
     */
    static validateAllFormFields(formGroup: FormGroup) {
        if (formGroup && formGroup.controls && Object.keys(formGroup.controls).length) {
            formGroup.markAllAsTouched();
            Object.keys(formGroup.controls).forEach((field) => {
                const control = formGroup.get(field) as FormGroup;
                if (control && 'controls' in control && control.controls && Object.keys(control.controls).length) {
                    AppHelper.validateAllFormFields(control);
                } else {
                    if (control) {
                        control.markAsTouched({ onlySelf: true });
                        control.setErrors(control.errors);
                    }
                }
            });
        }
    }

    /**
     * Получение кнопок для таблицы по доступам
     * @param buttons кнопки таблицы
     * @param access доступы
     * @param service сервис системы
     * @param addButton добавленные кнопки
     */
    static getCreateAccessTableButton(
        buttons: ITableButtons,
        access: any,
        service: AppService,
        addButton: { [key: string]: boolean } = {},
    ): ITableButtons {
        if ((access?.show || access?.edit) && addButton.edit) {
            const edit: IButton = {
                records: service.isMassEditAllowed ? 'many' : 'one',
                icon: (!access?.edit || access?.edit === 'undefined') && access?.show ? 'eye' : 'pencil',
                name:
                    (!access?.edit || access?.edit === 'undefined') && access?.show
                        ? service.language_dictionary.system_look_inside
                        : service.language_dictionary.system_edit,
                click: 'edit',
                type: 'edit',
            };
            buttons.globalMenu.push(edit);
            buttons.local.unshift(edit);
        }

        if (access?.show && addButton.show) {
            const show: IButton = {
                records: 'one',
                icon: 'eye',
                name: service.language_dictionary.system_look_inside,
                click: 'edit',
                type: 'edit',
            };
            buttons.globalMenu.push(show);
            buttons.local.unshift(show);
        }
        // Добавление кнопки для реестра документов (Список всех материалов по заявлению) "Посмотреть файл"
        if (addButton.file) {
            const addOnService = {
                icon: 'paperclip',
                name: service.language_dictionary.list_of_all_application_materials_show_file,
                click: 'showFile',
            };
            buttons.local.push(addOnService);
        }
        if (access?.add && addButton.addRecord) {
            buttons.global = [
                {
                    icon: 'plus',
                    name: service.language_dictionary.system_add,
                    click: 'add',
                },
            ];
        }

        if ((access?.factor_moderation || access?.conditional_moderator) && addButton.accept_the_report) {
            const accept: IButton = {
                name: service.language_dictionary.factor_to_accept,
                icon: 'receipt-text-check',
                click: 'acceptTheReport',
                records: 'many',
                isShow: (rows: { [key: string]: any; status?: EFactorDataStatuses }[]) => {
                    // Не для всех статусов карточки можно отображать кнопку
                    return rows?.every(
                        (row) =>
                            row?.status === EFactorDataStatuses.Moderation ||
                            row?.status === EFactorDataStatuses.PreModeration ||
                            row?.status === EFactorDataStatuses.Reject,
                    );
                },
            };
            buttons.globalMenu.push(accept);
            buttons.local.push(accept);
        }
        if ((access?.factor_moderation || access?.conditional_moderator) && addButton.reject_the_report) {
            const reject: IButton = {
                name: service.language_dictionary.factor_decline,
                click: 'rejectTheReport',
                icon: 'receipt-text-remove',
                records: 'many',
                isShow: (rows: { [key: string]: any; status?: EFactorDataStatuses }[]) => {
                    // Не для всех статусов карточки можно отображать кнопку
                    return rows?.every(
                        (row) =>
                            row?.status === EFactorDataStatuses.Moderation ||
                            row?.status === EFactorDataStatuses.PreModeration,
                    );
                },
            };

            buttons.globalMenu.push(reject);
            buttons.local.push(reject);
        }
        if (addButton.addOnService) {
            const addOnService = {
                icon: 'plus',
                name: service.language_dictionary.add_on_service,
                click: 'addOnService',
            };
            buttons.globalMenu.push(addOnService);
            buttons.local.push(addOnService);
        }
        if (access?.edit && (addButton.copy || addButton.copyCreate)) {
            const copyAndCreate: IButton = {
                records: 'one',
                icon: 'content_copy',
                name: service.language_dictionary.factor_copy,
                click: 'copyAndCreate',
            };
            buttons.globalMenu.push(copyAndCreate);
            buttons.local.push(copyAndCreate);
        }
        // Кнопка импорта с условиями отображения
        if (access?.show && addButton.import) {
            const edit = {
                icon: 'database-import-outline',
                name: service.language_dictionary.import,
                title: service.language_dictionary.import,
                click: 'import',
                isButtonsPanel: true,
            };
            buttons.globalMenu.push(edit);
        }

        if (access?.show && addButton.export) {
            const edit = {
                icon: 'database-export',
                name: service.language_dictionary.factor_export,
                title: service.language_dictionary.factor_export,
                click: 'export',
                isButtonsPanel: true,
            };
            buttons.globalMenu.push(edit);
        }
        if (addButton.changeSubject) {
            const changeSubject: IButton = {
                records: 'many',
                icon: 'compare',
                name: service.language_dictionary.registry_change_subject,
                click: 'changeSubject',
            };
            buttons.globalMenu.push(changeSubject);
            buttons.local.push(changeSubject);
        }
        if (access?.show && access?.edit && addButton.viewSection) {
            const viewSection: IButton = {
                records: 'one',
                icon: 'eye',
                name: service.language_dictionary.view_section,
                click: 'viewSection',
            };
            buttons.globalMenu.push(viewSection);
            buttons.local.push(viewSection);
        }

        if ((access?.del || access?.delete) && addButton.deleteForPeriod) {
            const deleteForPeriod = {
                icon: 'delete-clock-outline',
                name: service.language_dictionary.logevent_journal_del_for_a_period,
                click: 'deleteForPeriod',
            };
            buttons.globalMenu.push(deleteForPeriod);
            buttons.local.push(deleteForPeriod);
        }
        if (access?.edit && addButton.activated) {
            const activated: IButton = {
                records: 'many',
                icon: 'check-circle',
                name: service.language_dictionary.user_list_activate,
                click: 'activated',
                disabled: {
                    field: 'active',
                    condition: '1',
                },
            };
            buttons.globalMenu.push(activated);
            buttons.local.push(activated);
        }
        if (access?.edit && addButton.deactivated) {
            const deactivated: IButton = {
                records: 'many',
                icon: 'close-circle-outline',
                name: service.language_dictionary.deactivate,
                click: 'deactivated',
                disabled: {
                    field: 'active',
                    condition: '',
                },
            };
            buttons.globalMenu.push(deactivated);
            buttons.local.push(deactivated);
        }
        if (access?.edit && addButton.newLayer) {
            const newLayer: IButton = {
                records: 'one',
                icon: 'plus-box-multiple',
                name: service.language_dictionary.external_add_layer,
                click: 'addLayer',
            };
            buttons.globalMenu.push(newLayer);
            buttons.local.push(newLayer);
        }
        if (access?.edit && addButton.searchDuplicates) {
            const searchDuplicates: IButton = {
                records: 'one',
                icon: 'text-box-search',
                name: service.language_dictionary.search_for_duplicates,
                click: 'searchDuplicates',
            };
            buttons.globalMenu.push(searchDuplicates);
            buttons.local.push(searchDuplicates);
        }
        if (access?.show && addButton.generateReport) {
            const generateReport = {
                icon: 'text-box-plus',
                name: service.language_dictionary.factor_regulated_report,
                click: 'generateReport',
            };
            buttons.globalMenu.push(generateReport);
            buttons.local.push(generateReport);
        }
        // Настройка фильтра регламентированного отчета
        if (access?.edit && addButton.openReportFilter) {
            const remove: IButton = {
                records: 'one',
                icon: 'filter',
                name: service.language_dictionary.system_setting_filter,
                click: 'getReportFilterSettings',
            };
            buttons.globalMenu.push(remove);
            buttons.local.push(remove);
        }
        if (access?.show && addButton.read) {
            const read: IButton = {
                records: 'many',
                icon: 'email_open',
                name: service.language_dictionary.mark_as_read,
                click: 'readArray',
                // Деактивация кнопки при заданном условии
                disabled: {
                    field: 'read_status',
                    condition: 'read',
                },
            };
            buttons.globalMenu.push(read);
            buttons.local.push(read);
        }
        // Экспорт из таблицы
        if (access?.show && addButton.exportPdf) {
            const read: IButton = {
                records: 'many',
                icon: 'file-pdf-box',
                name: service.language_dictionary.factor_export,
                click: 'exportPdf',
            };
            buttons.globalMenu.push(read);
            buttons.local.push(read);
        }
        // Просмотр отчета
        if (access?.show && addButton.showReport) {
            const read: IButton = {
                records: 'one',
                icon: 'eye',
                name: service.language_dictionary.system_look_inside,
                click: 'showReport',
            };
            buttons.globalMenu.push(read);
            buttons.local.push(read);
        }
        if ((access?.del || access?.delete) && addButton.del) {
            const remove: IButton = {
                records: 'many',
                icon: 'delete',
                type: 'delete',
                name: service.language_dictionary.system_del,
                click: 'deleteArray',
            };
            buttons.globalMenu.push(remove);
            buttons.local.push(remove);
        }
        // Кастомное удаление с доп. опциями
        if ((access?.del || access?.delete) && addButton.customDelete) {
            const remove: IButton = {
                records: 'many',
                icon: 'delete',
                type: 'delete',
                name: service.language_dictionary.system_del,
                click: 'customDelete',
            };
            buttons.local.push(remove);
            buttons.globalMenu.push(remove);
        }
        // Нельзя удалять если роль is_required
        if ((access?.del || access?.delete) && addButton.delRoleCondition) {
            const remove: IButton = {
                records: 'many',
                icon: 'delete',
                type: 'delete',
                name: service.language_dictionary.system_del,
                click: 'deleteArray',
                disabled: {
                    field: 'is_required',
                    condition: '1',
                },
            };
            buttons.globalMenu.push(remove);
            buttons.local.push(remove);
        }
        return buttons;
    }

    /**
     * Проставление валидации в поле
     * @param property поле формы
     * @param data данные по ошибке
     */
    static setErrorField(property: any, data: any) {
        if (property && property.controls && Object.keys(property.controls).length) {
            if (Object.keys(data).length) {
                Object.keys(data).forEach((field) => {
                    AppHelper.setErrorField(property?.get(field), data[field]);
                });
            }
        } else {
            property?.setErrors({
                serverError: data,
            });
        }
    }

    /**
     * Вывод ошибок формы
     * @param result результат запроса
     * @param form объект формы
     * @param formRef ссылка на форму
     * @param notifier сервис всплывающих сообщений
     */
    static setErrorForm(result: any, form: FormGroup, formRef: ElementRef | any, notifier: NotifierService | null) {
        if (result.message) {
            notifier?.notify('error', result.message);
        }

        if (result?.data && Object.keys(result?.data)?.length) {
            for (const field in result.data) {
                if (result.data.hasOwnProperty(field) && result.data[field]) {
                    AppHelper.setErrorField(form?.get(field), result.data[field]);
                }
            }
            this.focusFirstInvalidField(formRef);
        }
    }

    /**
     * Создание мультиязычного поля
     * @param appService сервис с переводами
     * @param formBuilder создатель форм
     * @param form форма
     * @param field поле
     * @param data данные
     * @param required обязательность поля
     */
    static createLangField(
        appService: AppService,
        formBuilder: FormBuilder,
        form: FormGroup,
        field: string,
        data: any,
        required: boolean,
    ) {
        Object.keys(appService.languages).forEach((lang) => {
            form.addControl(
                appService.languages[lang].lang,
                formBuilder.control(
                    data && data[field] && data[field][appService.languages[lang].lang]
                        ? data[field][appService.languages[lang].lang]
                        : '',
                    appService.languages[lang].lang === appService.defaultLanguage
                        ? required
                            ? [Validators.required]
                            : []
                        : [],
                ),
                { emitEvent: false },
            );
        });
    }

    /**
     * Получение текущего языка
     * @param appService сервис с переводами
     */
    static getCurrentLanguage(appService: AppService): IAppServiceLanguages {
        let currentLang: IAppServiceLanguages = null;
        Object.keys(appService.languages).forEach((lang) => {
            if (appService.languages[lang].lang === appService.currentLanguage) {
                currentLang = appService.languages[lang];
            }
        });
        return currentLang;
    }

    /**
     * Добавление поля на форму
     * @param appService сервис с переводами
     * @param mainForm форма
     * @param dynamicControls список контроллов
     * @param formBuilder создатель форм
     * @param id ид элемента
     * @param type тип элемента
     * @param value значение
     * @param isChangeName - флаг изменять имя контрола
     */
    static addFormDynamicField(
        appService: AppService,
        mainForm: FormGroup,
        dynamicControls: any,
        formBuilder: FormBuilder,
        id: number | string,
        type: string,
        value: any,
        isChangeName: boolean = true,
    ) {
        if (id == null) {
            id = AppHelper.getKeyField();
        }
        // Проверяем, есть ли уже группа условий в форме
        if (mainForm.get(type) == null) {
            mainForm.addControl(type, formBuilder.group({}));
        }
        const form = mainForm.get(type) as FormGroup;
        const formGroup = formBuilder.group({});
        const name = (isChangeName ? type + '_' : '') + id;
        if (!dynamicControls[type].simple) {
            form.addControl(name, formGroup);
        }
        // Добавляем поля
        if (dynamicControls[type].fields && dynamicControls[type].fields.length) {
            dynamicControls[type].fields.forEach((field) => {
                formGroup.addControl(
                    field.name,
                    new FormControl(value && value[field.name] ? value[field.name] : null, {
                        validators: field.validation,
                    }),
                );
            });
        }
        // Если есть языковые поля
        if (dynamicControls[type].fieldsLang && dynamicControls[type].fieldsLang.length) {
            dynamicControls[type].fieldsLang.forEach((field) => {
                const formGroupLang = formBuilder.group({});
                formGroup.addControl(field.name, formGroupLang);
                AppHelper.createLangField(
                    appService,
                    formBuilder,
                    formGroupLang,
                    field.name,
                    value,
                    !!field?.validation?.length,
                );
            });
        }
        if (dynamicControls[type].simple) {
            form.addControl(
                name,
                new FormControl(value ? value : null, {
                    validators: dynamicControls[type].validation,
                }),
            );
        }
        return {
            field_name: name,
            id,
            value,
            type,
        };
    }
    /**
     * Получение ключа поля
     */
    static getKeyField() {
        return new Date().getTime() + '_' + AppHelper.randomInteger(1, 999999);
    }

    /**
     * Случайное число
     * @param min минимальное число
     * @param max максимальное число
     */
    static randomInteger(min, max) {
        // Получить случайное число от (min-0.5) до (max+0.5)
        const rand = min - 0.5 + Math.random() * (max - min + 1);
        return Math.round(rand);
    }

    /**
     * Удаление динамического поля
     * @param mainForm элемент формы
     * @param id ид элемента
     * @param type тип элемента
     */
    static removeFormDynamicField(mainForm: FormGroup, id: number | string, type: string) {
        const form = mainForm.get(type) as FormGroup;
        form.removeControl(id as string);
    }

    /**
     * Получение данных динамического поля
     * @param data данные формы
     * @param dynamicControls набор динамических полей
     * @param fieldValue по какому полю брать значение
     */
    static getDataFormDynamicFields(data: any, dynamicControls: any, fieldValue: string[] = null) {
        for (const field in dynamicControls) {
            if (dynamicControls.hasOwnProperty(field)) {
                const dataValue = [];
                for (const control in data[field]) {
                    if (data[field].hasOwnProperty(control)) {
                        if (dynamicControls[field].simple && data[field][control]) {
                            dataValue.push(data[field][control]);
                        }
                        if (
                            dynamicControls[field].fields &&
                            !fieldValue &&
                            data[field][control] &&
                            data[field][control].value
                        ) {
                            dataValue.push(data[field][control]);
                        }
                        if (dynamicControls[field].fields && fieldValue && data[field][control]) {
                            let isData = false;
                            fieldValue.forEach((value) => {
                                if (data[field][control][value]) {
                                    isData = true;
                                }
                            });
                            if (isData) {
                                dataValue.push(data[field][control]);
                            }
                        }
                    }
                }
                data[field] = dataValue;
            }
        }
        return data;
    }

    /**
     * Получение описания статуса записи
     * @param status статус записи
     * @param appService сервис с переводами
     */
    static getStatusInfo(status: string, appService: AppService) {
        let text = appService.language_dictionary.factor_not_filled_single;
        let color = 'gray';
        let icon = 'fiber_manual_record';
        switch (status) {
            case 'approved':
                text = appService.language_dictionary.factor_accepted;
                color = 'green';
                icon = 'fiber_manual_record';
                break;
            case 'reject':
                text = appService.language_dictionary.factor_not_accepted;
                color = 'red';
                icon = 'fiber_manual_record';
                break;
            case 'moderation':
                text = appService.language_dictionary.system_under_consideration;
                color = 'orange';
                icon = 'fiber_manual_record';
                break;
            case 'draft':
            case 'fill':
                text = appService.language_dictionary.factor_saved;
                color = 'orange';
                icon = 'fiber_manual_record';
                break;
        }
        return {
            text,
            color,
            icon,
        };
    }

    /**
     * Всплытие до момента нахождения родительского элемента
     * @param target элемент
     */
    static getParentElement(target) {
        const parent = target.parentElement;
        const position = parent.getBoundingClientRect();
        if (position.top === 0) {
            return this.getParentElement(parent);
        }
        return position.top;
    }

    /**
     * Получение размера файла в нужных единицах
     * @param size - размер файла в байтах
     * @param unit - в какой кратности выводить
     * @param round - количество цифр после запятой
     * @return - вес файла в нужном формате
     */
    static calcFileSize(size: number, unit: string, round: number = 1): number {
        if (size < 0) {
            return 0;
        }

        let unitDegree: number = 0;

        switch (unit) {
            case 'Gb':
                unitDegree = 3;
                break;
            case 'Mb':
                unitDegree = 2;
                break;
            case 'Kb':
                unitDegree = 1;
                break;
        }

        return Math.round((size / Math.pow(1024, unitDegree)) * Math.pow(10, round)) / Math.pow(10, round);
    }

    /**
     * Сортировка
     * @param field - поля, по которым сортируем
     * @param order - направление сортировки
     * arr.sort(compare()); //Обычная типобезопасная сортировка по возрастанию
     * arr.sort(compare(-1)); //Обычная типобезопасная сортировка по убыванию
     * arr.sort(compare('field')); //Сортировка по свойству field по возрастанию
     * arr.sort(compare('field', -1)); //Сортировка по свойству field по убыванию
     *  //Сортировка сначала по полю field1
     *    при совпадении по полю field2, а если и оно совпало, то по полю field3
     *    все по возрастанию
     * arr.sort(compare('field1', 'field2', 'field3'));
     * //Сортировка сначала по полю field1 по возрастанию
     *    при совпадении по полю field2 по убыванию
     *    arr.sort(compare({
     *     field1 : 1,
     *     field2 : -1
     * }));
     */
    static compare(field, order = 1) {
        let len = arguments.length;
        if (len === 0) {
            return (a, b) => (a < b && -1) || (a > b && 1) || 0;
        }
        const collator = new Intl.Collator(AppHelper.getCollatorLocales(), {});
        if (len === 1) {
            switch (typeof field) {
                case 'number':
                    return field < 0
                        ? (a, b) => (a < b && 1) || (a > b && -1) || 0
                        : (a, b) => (a < b && -1) || (a > b && 1) || 0;
                case 'string':
                    return (a, b) =>
                        (a[field].toLowerCase() < b[field].toLowerCase() && -1) ||
                        (a[field].toLowerCase() > b[field].toLowerCase() && 1) ||
                        0;
            }
        }
        if (len === 2 && typeof order === 'number') {
            return order < 0
                ? (a, b) => (a[field] < b[field] && 1) || (a[field] > b[field] && -1) || 0
                : (a, b) => (a[field] < b[field] && -1) || (a[field] > b[field] && 1) || 0;
        }
        let fields;
        let orders;
        if (typeof field === 'object') {
            fields = Object.getOwnPropertyNames(field);
            orders = fields.map((key) => field[key]);
            len = fields.length;
        } else {
            fields = new Array(len);
            orders = new Array(len);
            for (let i = len; i--; ) {
                fields[i] = arguments[i];
                orders[i] = 1;
            }
        }

        return (a, b) => {
            for (let i = 0; i < len; i++) {
                if (typeof a[fields[i]] === 'string' || typeof b[fields[i]] === 'string') {
                    if (collator.compare(a[fields[i]].toLowerCase(), b[fields[i]].toLowerCase()) > 0) {
                        return -orders[i];
                    }
                    return orders[i];
                } else {
                    if (a[fields[i]] < b[fields[i]]) {
                        return orders[i];
                    }
                    if (a[fields[i]] > b[fields[i]]) {
                        return -orders[i];
                    }
                }
            }
            return 0;
        };
    }

    /** Получить локали для сравнения строк при сортировке */
    static getCollatorLocales(): string[] {
        const locales = ['en-US'];
        const a = new TokenStorage();
        switch (a?.getCurrentLang()?.toLowerCase()) {
            case 'ru':
                locales.push('ru-RU');
                break;
        }
        return locales;
    }

    /**
     * Обработка строки перед поиском
     * @param str - строка, которую будут искать
     */
    static escapingStringForSearch(str: string): string {
        return String(str ?? '')
            .trim()
            .toLowerCase()
            .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            .replace(/[еёЕЁ]/g, '[еёЕЁ]');
    }

    /** Определяем, применяется ли text-overflow: ellipsis
     * @param element - html-элемент
     *
     * Нам понадобится element, обязательно имеющий ширину и стили для ellipsis (см. миксин ellipsis)
     * и его дочерний элемент (display: inline) куда нужно разместить контент.
     * Если ширина дочернего элемента будет больше или равна родительскому, сработает
     * ellipsis и метод вернет true.
     */
    static isEllipsisActive(element: HTMLElement) {
        const width = element?.offsetWidth;
        const child = element?.firstElementChild as HTMLElement;
        const widthChild = child?.offsetWidth;
        return widthChild >= width + 4;
    }

    /**
     * Получить сообщение об ошибке
     * @param error - ошибка
     * @param defaultMessage - сообщение по умолчанию
     */
    static getErrorMessage(error: Error | HttpErrorResponse, defaultMessage: string = ''): string {
        if (error instanceof HttpErrorResponse && error?.error?.hasOwnProperty('message')) {
            return error?.error?.message;
        } else if (error instanceof HttpErrorResponse || error instanceof Error) {
            return error?.message;
        }
        return defaultMessage;
    }

    /** Метод проверки аутентификации пользователя */
    static isAuth(): boolean {
        const token = new TokenStorage();

        return token.getToken() && token.getToken()?.length > 0;
    }

    /** Метод для получения Outlet для роута */
    static getOutlet(): string {
        return AppHelper.isAuth() ? 'primary' : 'public';
    }

    /**
     * Сортировка элементов, не зависимо от типа индекса (строка или число)
     * @param a - первый элемент
     * @param b - следующий элемент
     */
    static valueAscOrder = (a: KeyValue<number | string, string>, b: KeyValue<number | string, string>): number => {
        return String(a.key).localeCompare(String(b.value));
    };

    /**
     * Создать форму сортировки
     * @param data - данные формы сортировки
     * @param fb
     * @private
     */
    static createSortForm(data: ISort[], fb: FormBuilder): FormArray<AbstractControl<ISort, ISort>> {
        const form = new FormArray([], [Validators.required, uniqueFieldValidator]);

        data.filter((item) => item?.field).forEach((item) => {
            const subForm = fb.group({
                field: [String(item.field ?? null), [Validators.required]],
                order: [item.order === SortOrder.ASK ? SortOrder.ASK : SortOrder.DESC, [Validators.required]],
            });
            form.push(subForm);
        });

        return form;
    }

    /**
     * Отлавливаем ошибку и отправляем пользователя на соответствующую страничку
     * @param error - объект, содержащий описание ошибки
     * @param router - класс для маршрутизации по приложению
     */
    static catchHttpErrorResponse = (error: HttpErrorResponse, router: Router) => {
        switch (error.status) {
            case 404:
            case 403:
                router.navigate(['page-not-found', error.status]).then();
        }
        return throwError(() => new Error(error?.message));
    };

    /**
     * Проверка массивов на равенство (без учета порядка элементов)
     * @param array1
     * @param array2
     */
    static arraysEqualUnordered<T>(array1: T[], array2: T[]): boolean {
        if (!Array.isArray(array1) || !Array.isArray(array2)) {
            return false;
        }
        if (array1.length !== array2.length) {
            return false;
        }

        const sortedArr1 = array1.slice().sort(); // Сортируем первый массив
        const sortedArr2 = array2.slice().sort(); // Сортируем второй массив

        for (let i = 0; i < sortedArr1.length; i++) {
            if (sortedArr1[i] !== sortedArr2[i]) {
                return false;
            }
        }

        return true; // Все элементы равны
    }

    /**
     * Содержит ли хотя бы один элемент из массива в другом массиве
     * @param array1
     * @param array2
     */
    static hasCommonElement<T>(array1: T[], array2: T[]): boolean {
        if (!Array.isArray(array1) || !Array.isArray(array2)) {
            return false;
        }

        for (const element of array1) {
            if (array2.includes(element)) {
                return true; // Элемент найден в обоих массивах
            }
        }
        return false; // Нет общих элементов
    }

    /**
     * Устанавливает фокус на первое невалидное поле
     * @param target ссылка на элемент формы
     * @param selectors список селекторов для фокуса
     */
    static focusFirstInvalidField(target: ElementRef | any, selectors?: string[]) {
        if (!target) {
            return;
        }

        selectors ??= this.invalidSelectors;

        if (target.nativeElement) {
            this.focusInput(target.nativeElement.querySelector(selectors.join(',')));
            return;
        }
        this.focusInput(target.querySelector(selectors.join(',')));
    }
}
